<template>
  <i-modal :title="LANG.listVariants" @openAutoFocus="fetchVariants">
    <template #default>
      <Loading v-if="loading"></Loading>
      <div v-else-if="error">
        <span class="error">{{ message }}</span>
      </div>
      <div v-else class="mam-variants-wrapper">
        <div class="mam-variants-list">
          <table class="filelist">
            <thead>
              <tr>
                <th class="star-cell"></th>
                <th class="size-cell">{{ LANG.size }}</th>
                <th class="descr-cell">{{ LANG.description }}</th>
                <th class="actions-cell">{{ LANG.actions }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="{
                    file,
                    uri,
                    variant_description: description,
                    description: placeholder,
                    is_default,
                    width,
                    height,
                    action
                  } in variants"
                :key="uri"
                class="clickable-row"
                :class="{'i-state-active': uri === selected.uri}"
                @click="() => selectVariant(uri)"
              >
                <td>
                  <template v-if="is_default">
                    <i class="fas fa fa-star"></i>
                    <span class="sr-only">{{ LANG.defaultImage }}</span>
                  </template>
                </td>
                <td :class="{ 'mam-variant-thumbnail-size': action == 'Thumbnail' }">{{ width }} x {{ height }}</td>
                <td class="descr-cell">
                  <i-text-field-editable
                    :modelValue="description"
                    @update:modelValue="(description) => setDescription(file, uri, description)"
                    :placeholder="placeholder"
                    :readonly="uri === master.uri"
                  />
                </td>
                <td class="actions-cell">
                  <i-dropdown>
                    <template v-slot:trigger="{toggle, attrs}">
                        <a
                            href="#"
                            class="ph1 outline-none inline-block trigger"
                            @click.prevent.stop="toggle"
                            v-bind="attrs"
                            aria-haspopup='true'
                            :title='LANG.moreOptionsLabel'
                        >
                            <i aria-hidden="true" :title="LANG.moreOptionsLabel" class="fas fa-ellipsis-h" name="ellipsis-h"></i>
                            <span class='sr-only'>{{LANG.moreOptionsLabel}}</span>
                        </a>
                    </template>
                    <i-menu-list class="pull-right overflow-y-auto">
                      <i-menu-action
                        :label="LANG.delete"
                        icon="times"
                        :href="deleteVariantLink(uri)"
                      />
                      <i-menu-action
                        :label="LANG.setDefault"
                        icon="star"
                        @click.prevent="() => setDefault(file)"
                      />
                    </i-menu-list>
                  </i-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="mam-variant-preview">
            <h4>{{ LANG.preview }}</h4>
            <div class="image-container">
                <img :src="imageUriVersion" alt="" />
            </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div>
        <i-button
          variant="secondary"
          @click.prevent="$emit('update:show', false)"
        >
          {{ LANG.cancel }}
        </i-button>
        <i-button
          v-if="insertMode"
          variant="primary"
          @click.prevent="injectVariant"
        >
          {{ LANG.insertVariant }}
        </i-button>
      </div>
    </template>
  </i-modal>
</template>

<script>
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import { mapBrowserActions } from '@/store';
import fetchService from '@/utils/model/fetch_service';
import { ControllerParamFormat } from '@/components/browser/panel_center/PanelCenterFooterMassActions.vue';
import { FinishController } from '@/store/modules/entity_browser/config';
import globals from '@/globals';

import Loading from '@/components/common/Loading.vue';

export default defineComponent({
  components: {
    Loading,
  },
  props: {
    document: {
      default() {
        return {};
      },
      type: Object,
    },
    insertMode: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    master: {},
    variants: [],
    selected: {},
    loading: true,
    error: false,
    message: '',
  }),
  computed: {
    ...mapState(['LANG', 'browserMode']),
    previewVersion() {
      return btoa(this.document.finished_at);
    },
    imageUriVersion() {
      if (!this.selected.uri && !this.master.uri) {
        return '#';
      }

      return `${this.selected.uri || this.master.uri}?v=${this.previewVersion}`;
    },
  },
  methods: {
    ...mapBrowserActions(['insertVariant']),
    async fetchVariants() {
      this.loading = true;
      const response = await fetchService.GetData('docbrowser_variants', { __imperia_node_id: this.document.nodeId });
      this.error = !!response.error;
      this.message = response.message;

      if (!this.error) {
        this.variants = response.data.variants;
        this.master = response.data.master;

        // Select the first variant by default
        const [first] = this.variants;
        this.selected = first;
      }

      this.loading = false;
    },
    async setDefault(file) {
      const response = await fetchService.GetData('docbrowser_variants_set_default', { __imperia_node_id: this.document.nodeId, default: file, fromTemplate: 1 });
      this.error = !!response.error;
      this.message = response.message;

      if (!response.error) {
        const oldDefault = this.variants.find((variant) => Boolean(Number(variant.is_default)) === true);
        if (oldDefault) {
          oldDefault.is_default = false;
        }

        const newDefault = this.variants.find((variant) => variant.file === file);
        if (newDefault) {
          newDefault.is_default = true;
        }
      }
    },
    setDescription(file, uri, description) {
      const variant = this.variants.find((v) => v.uri === uri) || {};
      variant.variant_description = description;

      fetchService.GetData('docbrowser_variants_set_description', {
        __imperia_node_id: this.document.nodeId,
        variant_filename: file,
        variant_uri: uri,
        variant_description: description,
      });
    },
    deleteVariantLink(url) {
      return globals.I.Util.uri('/document/media/content/published/mass_action/delete', {
        ...ControllerParamFormat.delete({ nodeId: this.document.nodeId, url }),
        finish_controller: FinishController[this.browserMode],
      });
    },
    selectVariant(uri) {
      this.selected = this.variants.find((v) => v.uri === uri) || {};
    },
    injectVariant() {
      this.insertVariant({
        nodeId: this.document.nodeId,
        variantUri: this.selected.uri,
        contentLength: this.document.filesize,
      });
    },
  },
});
</script>

<style scoped lang="scss">
  .loader {
    margin: 0 auto;
  }

  .error {
    color: $red;
  }

  .mam-variants-list, .mam-variant-preview {
      width: calc(50% - 10px);
      float: left;
  }
  .mam-variants-list {
      margin-right: 10px;
      max-height: 530px;
  }
  .mam-variant-preview {
      margin-left: 10px;
  }
  .mam-variants-list .actions-cell {
      text-align: right;
  }
  .mam-variants-list .actions-cell > * {
      margin-left: 5px;
      margin-right: 5px;
  }
  .mam-variants-list .fa5, .mam-variants-list .fa,
  .mam-variants-list a .fa5, .mam-variants-list a .fa {
      color: $blue;
  }
  .mam-variant-preview h4 {
      color: #000;
      font-family: $font-family-regular;
      font-weight: 500;
      margin: 13px 3px 12px 5px;
      font-size: 13px;
      padding: 0;
  }
  .mam-variant-preview .image-container {
      background: $i-list-border-color;
      width: 100%;
      min-height: 220px;
      line-height: 220px;
      text-align: center;
      vertical-align: middle;
  }
  .mam-variant-preview .image-container img {
      max-width: 100%;
      vertical-align: middle;
  }
  .actions-cell {
      width: 50px;
  }
  .size-cell {
      width: 100px;
  }
  .star-cell {
      width: 40px;
  }
  .descr-cell {
      position: relative;
      padding-right: 25px;
  }
  .mam-variants-list .filelist tr.clickable-row:hover {
      background: $i-list-hover-bg;
  }
  .mam-variant-thumbnail-size {
    font-style: italic;
  }
</style>
